import {Textarea} from "@chakra-ui/react";
import type {TextareaProps} from "@chakra-ui/react";
import React, {useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef} from "react";
import {useResizeObserver} from "usehooks-ts";

export type AutoResizingTextareaProps = {minRows?: number; maxRows?: number};

type ScrollSnapshot = {
  elements: {node: HTMLElement; top: number; left: number}[];
  window: {win: Window; x: number; y: number} | null;
};

/** Record the scroll offsets of every scrollable ancestor, up to and including the window. */
function snapshotScroll(el: HTMLElement): ScrollSnapshot {
  const elements: ScrollSnapshot["elements"] = [];

  for (let node = el.parentElement; node; node = node.parentElement) {
    if (node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth) {
      elements.push({node, top: node.scrollTop, left: node.scrollLeft});
    }
  }

  const win = el.ownerDocument.defaultView;
  return {elements, window: win ? {win, x: win.scrollX, y: win.scrollY} : null};
}

function restoreScroll({elements, window}: ScrollSnapshot) {
  for (const {node, top, left} of elements) {
    // Force an instant jump back, otherwise an ancestor with `scroll-behavior:
    // smooth` would animate the restore.
    const previous = node.style.scrollBehavior;
    node.style.scrollBehavior = "auto";

    node.scrollTop = top;
    node.scrollLeft = left;

    node.style.scrollBehavior = previous;
  }

  window?.win.scrollTo({left: window.x, top: window.y, behavior: "auto"});
}

/**
 * Resize a textarea to fit its content.
 *
 * Scroll snapshot explanation:
 *
 * The field must be collapsed (`height: auto`) so `scrollHeight` reports the
 * natural content height rather than the currently-set height.
 *
 * `height: auto` collapses the text area to the size implied by its `rows`
 * attribute (`minRows` in Chakra). This could be much smaller than the original
 * height. If the parent container's `scrollTop` is now larger than its new
 * maximum allowed `scrollTop`, the browser immediately clamps it.
 *
 * Once we've measured the intended height of the text area and apply it, the
 * container's `scrollTop` is not restored automatically, leaving the scroll
 * position in the wrong place. This appears to interact with the "scroll
 * anchoring" behaviour in browsers sometimes causing the page to scroll *down*
 * as the browser tries to maintain the original scroll position.
 *
 * All-in-all, these problems are solved by first snapshotting the scroll
 * positions of all ancestors up to and including the window, performing the
 * resize, and then restoring the scroll positions afterwards.
 *
 * Preferably this would all be achieved using the `field-sizing: content` CSS
 * property, but unfortunately that's not yet supported in Firefox.
 *
 * Chakra's `autoresize` prop also does not handle this case, which is why I've
 * opted for a manual implementation. This approach is similar to that used in
 * the [autosize](https://github.com/jackmoore/autosize) library.
 */
function resizeToFitContent(el: HTMLTextAreaElement) {
  const snapshot = snapshotScroll(el);

  el.style.height = "auto";

  const style = el.ownerDocument.defaultView?.getComputedStyle(el);
  let height = el.scrollHeight;

  if (style) {
    if (style.boxSizing === "content-box") {
      height -= parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    } else {
      height += parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    }
  }

  el.style.height = `${height}px`;

  restoreScroll(snapshot);
}

export const AutoResizingTextarea = React.forwardRef<HTMLTextAreaElement, AutoResizingTextareaProps & TextareaProps>(
  ({minRows, maxRows, value, onChange, ...props}, ref) => {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    const resize = useCallback(() => {
      if (innerRef.current) resizeToFitContent(innerRef.current);
    }, []);

    // A controlled field re-renders on every value change, so the effect below
    // handles resizing; an uncontrolled one doesn't, so it resizes from onChange.
    // Tracking which avoids resizing twice per keystroke in the controlled case.
    const isControlled = value != null;

    // Resize on mount and whenever the (controlled) value changes.
    useLayoutEffect(() => {
      resize();
    }, [resize, value]);

    // Resize when the field's width changes, since that re-wraps the text.
    const lastWidth = useRef<number>();
    useResizeObserver({
      ref: innerRef,
      onResize: ({width}) => {
        if (width === lastWidth.current) return;
        lastWidth.current = width;
        resize();
      },
    });

    // Re-measure once web fonts finish loading, since that changes the text
    // metrics (and therefore the content height) after the initial render.
    useEffect(() => {
      const fonts = innerRef.current?.ownerDocument.fonts;
      if (!fonts) return;
      fonts.addEventListener("loadingdone", resize);
      return () => fonts.removeEventListener("loadingdone", resize);
    }, [resize]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(e);
        if (!isControlled) resize();
      },
      [onChange, resize, isControlled],
    );

    return (
      <Textarea
        ref={innerRef}
        value={value}
        onChange={handleChange}
        w="100%"
        lineHeight="inherit"
        resize="none"
        overflowX="hidden"
        overflowY={maxRows ? "auto" : "hidden"}
        rows={minRows ?? undefined}
        minH={minRows ? `${minRows}lh` : undefined}
        maxH={maxRows ? `${maxRows}lh` : undefined}
        {...props}
      />
    );
  },
);
